'use client'

import { memo, useEffect, useMemo, useRef, useState } from 'react'

import * as TabsPrimitive from '@radix-ui/react-tabs'
import { useQuery } from '@tanstack/react-query'
import {
  ColumnDef,
  ColumnFiltersState,
  createColumnHelper,
  OnChangeFn,
  PaginationState,
} from '@tanstack/react-table'
import { usePrevious } from '@uidotdev/usehooks'
import { isError, isNil } from 'lodash'
import { CopyIcon } from 'lucide-react'

import { AccountingBalanceSummary } from '~/app/accounting/AccountingBalanceSummary'
import { AccountingFilterBar } from '~/app/accounting/AccountingFilterBar'
import { ImportedAccountsTab } from '~/app/accounting/ImportedAccountsTab'
import { LotsTab } from '~/app/accounting/LotsTab'
import { PartialNetworkAccountingBanner } from '~/app/accounting/PartialNetworkAccountingBanner'
import { RecomputeLotsButton } from '~/app/accounting/RecomputeLotsButton'
import { TokensTab } from '~/app/accounting/TokensTab'
import {
  CostBasisFilterValue,
  EMPTY_COST_BASIS_FILTER,
} from '~/app/accounting/filters/CostBasisFilterButton'
import {
  DEFAULT_REMAINING_FILTER,
  RemainingFilterValue,
} from '~/app/accounting/filters/RemainingFilterButton'
import {
  EMPTY_UNIT_COST_FILTER,
  UnitCostFilterValue,
} from '~/app/accounting/filters/UnitCostFilterButton'
import {
  COLUMN_IDS,
  getColumnFilterValue,
  isValueFilterEmpty,
  parseDirectionAndValue,
} from '~/app/accounting/filters/helpers'
import { Period } from '~/app/accounting/period-utils'
import { NewReportDialog } from '~/app/accounting/reports/NewReportDialog'
import { ReportsTab } from '~/app/accounting/reports/ReportsTab'
import { useAccountingSearchParams } from '~/app/accounting/useAccountingSearchParams'
import { useLotRecompute } from '~/app/accounting/useLotRecompute'
import { buildCreatePath } from '~/app/accounts/create/createRoutes'
import { FormattedText } from '~/components/FormattedText'
import { HighlightedText } from '~/components/HighlightedText'
import { SplitsLink } from '~/components/Link'
import {
  Table,
  TableFilterMeta,
  TablePageSize,
  parseMultiSelectFilterValue,
} from '~/components/Table'
import {
  TABLE_ACTION_COLUMN_META,
  TABLE_ACTION_COLUMN_WIDTH,
  TableActionTrigger,
} from '~/components/TableActions'
import {
  TransactionForValue,
  TransactionValue,
} from '~/components/TransactionValue'
import { TruncateWithTooltip } from '~/components/TruncateWithTooltip'
import { withAuthentication } from '~/components/auth/withAuthentication'
import {
  Button,
  CurrencyAmount,
  PageLayout,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/components/component-library'
import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/component-library/Dropdown'
import { Timestamp } from '~/components/component-library/Timestamp'
import { TransactionTitle } from '~/components/component-library/TransactionTitle'
import { TransactionDropdownMenuItems } from '~/components/smart-accounts/TransactionDropdownMenuItems'
import { TransferDropdownMenuItems } from '~/components/smart-accounts/TransferDropdownMenuItems'
import { QUERY_KEYS } from '~/constants/react-query'
import { useAuth } from '~/context/AuthProvider'
import {
  useActiveOrg,
  useActiveOrgMember,
  useRequiredActiveOrg,
} from '~/hooks/org'
import { useAccountingTimezone } from '~/hooks/useAccountingTimezone'
import { useServerRequests } from '~/hooks/useRequests'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import {
  IAddress,
  OrgAssetTransfer,
  OrgRegularTransaction,
  OrgTransaction,
  TransactionStatus,
} from '~/types'
import { copyToClipboard } from '~/utils/copyToClipboard'
import { TransactionTitleChunk } from '~/utils/smartAccounts/titleChunks'
import { sendTrackingData } from '~/utils/tracking'

// This is a subset of the OrgRegularTransaction type because
// generating all the data from OrgRegularTransaction is too expensive
// for this view.
type AccountingTransaction = {
  id: OrgRegularTransaction['id']
  types: OrgRegularTransaction['types']
  displayChainId: OrgRegularTransaction['displayChainId']
  displayTokenAddress: OrgRegularTransaction['displayTokenAddress']
  displayTokenAmount: OrgRegularTransaction['displayTokenAmount']
  usdDisplayValue: OrgRegularTransaction['usdDisplayValue']
  usdNetValue: OrgRegularTransaction['usdNetValue']
  isOfframp: OrgRegularTransaction['isOfframp']
}

export type AccountingItem = {
  id: string
  date: string
  usdValue?: string
  realizedGain?: string | null
  smartAccountId: string
  smartAccountName: string
  smartAccountAddress: string
  erc20TokenAddress?: string
  erc20TokenSymbol?: string
  erc20TokenDecimals?: number
  chainId: number
  memo?: string
  transactionHash?: string
  flowType: 'inbound' | 'outbound' | 'none'
  titleChunks: TransactionTitleChunk[] | null
} & (
  | {
      isInbound: true
      relatedTransaction?: never
      assetTransfers?: never
      inboundTransfer: OrgAssetTransfer & {
        erc20TokenSymbol: string
        erc20TokenDecimals: number
      }
      userOpHash?: never
    }
  | {
      isInbound: false
      relatedTransaction: AccountingTransaction
      assetTransfers: Array<
        OrgAssetTransfer & {
          erc20TokenSymbol: string
          erc20TokenDecimals: number
        }
      >
      inboundTransfer?: never
      userOpHash: string
    }
)

const columnHelper = createColumnHelper<AccountingItem>()

const COLUMN_WIDTHS: Record<string, number> = {
  date: 140,
  title: 320,
  usdValue: 140,
  gains: 140,
  smartAccountId: 180,
  memo: 200,
  actions: TABLE_ACTION_COLUMN_WIDTH.size,
}

const INITIAL_PAGE_SIZE: TablePageSize = 25

const useAccountingColumns = () => {
  const { smartAccounts } = useSmartAccounts()
  const [tz] = useAccountingTimezone()
  const { featureFlags } = useActiveOrg()
  const showGains = !!featureFlags?.['accounting-v2']

  // Derive a stable key from actual values so this memo only re-runs when
  // account data genuinely changes, not when React Query returns a new
  // array reference with identical content (which cascades through columns
  // → Table → unmounts all rows).
  const smartAccountsKey = smartAccounts
    .map((a) => `${a.id}:${a.name}`)
    .join(',')
  const smartAccountsOptions = useMemo(() => {
    return smartAccounts.map((smartAccount) => ({
      value: smartAccount.id,
      display: <div>{smartAccount.name}</div>,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartAccountsKey])

  const unsizedColumns = useMemo(
    () => [
      columnHelper.accessor('date', {
        header: 'Date',
        id: 'date',
        cell: ({ getValue }) => (
          <span className="text-secondary">
            <Timestamp
              timestamp={getValue()}
              type="relative"
              abbreviate
              tz={tz}
            />
          </span>
        ),
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: 'title',
        header: 'Title',
        cell: ({ row }) => {
          const item = row.original
          return (
            <TransactionTitle
              transaction={{
                titleChunks: item.titleChunks,
                chainId: item.chainId,
              }}
              textWrap
              className="w-full min-w-0 whitespace-nowrap"
              tooltipOnTruncate
            />
          )
        },
      }),
      columnHelper.accessor('usdValue', {
        id: 'usdValue',
        header: 'USD value',
        cell: ({ row }) => {
          const item = row.original
          const transaction: TransactionForValue = item.inboundTransfer ?? {
            chainId: item.chainId,
            isSplitsTransaction: true,
            isPublicTransaction: false,
            types: item.relatedTransaction.types,
            displayChainId: item.relatedTransaction.displayChainId,
            displayTokenAddress: item.relatedTransaction.displayTokenAddress,
            displayTokenAmount: item.relatedTransaction.displayTokenAmount,
            usdDisplayValue: item.relatedTransaction.usdDisplayValue,
            usdNetValue: item.relatedTransaction.usdNetValue,
            isOfframp: item.relatedTransaction.isOfframp,
          }

          return (
            <TransactionValue
              transaction={transaction}
              showNetworkIcon={true}
              layout="standard"
              isSmall
            />
          )
        },
        meta: {
          align: 'right',
        },
      }),
      ...(showGains
        ? [
            columnHelper.accessor('realizedGain', {
              id: 'gains',
              header: 'Gain/loss',
              cell: ({ getValue }) => {
                const realizedGain = getValue()
                if (realizedGain === null || realizedGain === undefined) {
                  return <span className="text-secondary">-</span>
                }

                const value = Number(realizedGain)
                return (
                  <CurrencyAmount
                    layout="standard"
                    size="small"
                    value={value}
                    direction={
                      value > 0 ? 'inflow' : value < 0 ? 'outflow' : 'neutral'
                    }
                  />
                )
              },
              enableColumnFilter: false,
              meta: {
                align: 'right',
              },
            }),
          ]
        : []),
      columnHelper.accessor('smartAccountId', {
        id: 'smartAccountId',
        header: 'Account',
        cell: ({ row }) => {
          const { smartAccountAddress, smartAccountName } = row.original
          return (
            <TruncateWithTooltip
              content={smartAccountName}
              className="flex w-full items-center overflow-hidden"
            >
              <SplitsLink
                path={`/accounts/${smartAccountAddress}`}
                className="hover:underline"
              >
                <AddressDisplay
                  address={smartAccountAddress as IAddress}
                  layout="standard"
                  size="small"
                  hidePopover
                  isInternal
                />
              </SplitsLink>
            </TruncateWithTooltip>
          )
        },
        enableColumnFilter: true,
        // Filtering is done server-side
        meta: {
          filter: {
            variant: 'multiselect',
            label: 'Accounts',
            options: smartAccountsOptions,
          },
        } satisfies TableFilterMeta,
      }),
      columnHelper.accessor('memo', {
        id: 'memo',
        header: 'Memo',
        cell: ({ getValue, table }) => {
          const memo = getValue()
          const memoQuery = getColumnFilterValue(
            table.getState().columnFilters,
            COLUMN_IDS.MEMO,
          )

          return (
            <div className="flex w-full items-center gap-1">
              {memo ? (
                <TruncateWithTooltip
                  content={memo ? <FormattedText text={memo} /> : undefined}
                  className="w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
                >
                  <HighlightedText text={memo} highlight={memoQuery} />
                </TruncateWithTooltip>
              ) : (
                <span className="text-secondary">-</span>
              )}
            </div>
          )
        },
        // Filtering is done server-side
        meta: {
          filter: {
            label: 'Memo',
            variant: 'string',
          },
        } satisfies TableFilterMeta,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        ...TABLE_ACTION_COLUMN_WIDTH,
        cell: ({ row }) => {
          return (
            <div className="flex justify-end">
              <ActionsDropdown accountingItem={row.original} />
            </div>
          )
        },
        meta: TABLE_ACTION_COLUMN_META,
      }),
    ],
    [smartAccountsOptions, tz, showGains],
  )

  const columns = useMemo(
    () =>
      unsizedColumns.map((column) => ({
        ...column,
        size: COLUMN_WIDTHS[column.id!],
        minSize: COLUMN_WIDTHS[column.id!],
        // Don't set maxSize on memo column so it expands to fill remaining space
        ...(column.id !== 'memo' && {
          maxSize: COLUMN_WIDTHS[column.id!],
        }),
      })),
    [unsizedColumns],
  )

  return columns
}

const AccountingPageInner = () => {
  const {
    period,
    setPeriod,
    columnFilters,
    setColumnFilters,
    tab,
    setTab,
    filterTransactionsByToken,
    openLotsForToken,
  } = useAccountingSearchParams()

  const { org, featureFlags } = useActiveOrg()
  const showLots = !!featureFlags?.['accounting-v2']

  // Autocapture page views carry no org, and the adoption funnel counts orgs.
  // eslint-disable-next-line local/noUseEffect -- records the externally observable page-view event
  useEffect(() => {
    if (org?.id) sendTrackingData('accountingPageViewed', { orgId: org.id })
  }, [org?.id])

  // At page level so a rebuild lands on whichever tab is showing.
  const recompute = useLotRecompute({ enabled: showLots })

  // Client-side view filter for the Tokens tab — local state, not a URL param.
  const [tokenSearch, setTokenSearch] = useState('')
  // Server-side filters for the Lots tab — local state, not URL params.
  const [lotsSearch, setLotsSearch] = useState('')
  const [lotsRemaining, setLotsRemaining] = useState<RemainingFilterValue>(
    DEFAULT_REMAINING_FILTER,
  )
  const [lotsCostBasis, setLotsCostBasis] = useState<CostBasisFilterValue>(
    EMPTY_COST_BASIS_FILTER,
  )
  const [lotsUnitCost, setLotsUnitCost] = useState<UnitCostFilterValue>(
    EMPTY_UNIT_COST_FILTER,
  )

  const filterParams = useMemo(
    () => serializeColumnFilters(columnFilters, period ?? undefined),
    [columnFilters, period],
  )

  const accountIds = useMemo(
    () => filterParams.accountIds?.split(',').filter(Boolean),
    [filterParams.accountIds],
  )
  const chainIds = useMemo(
    () => filterParams.chainIds?.split(',').map(Number).filter(Number.isFinite),
    [filterParams.chainIds],
  )
  const tokenKeys = useMemo(
    () => filterParams.tokens?.split(',').filter(Boolean),
    [filterParams.tokens],
  )

  const hasValueFilter = useMemo(() => {
    const valueFilter = columnFilters.find((f) => f.id === COLUMN_IDS.USD_VALUE)
    if (!valueFilter?.value) return false
    const parsed = parseDirectionAndValue(valueFilter.value)
    if (isError(parsed)) return false
    return !isValueFilterEmpty(parsed)
  }, [columnFilters])

  // The lots and imported tabs only exist behind the flag; a stale URL
  // degrades to tokens.
  const effectiveTab =
    (tab === 'lots' || tab === 'imported') && !showLots ? 'tokens' : tab
  const isLots = effectiveTab === 'lots'
  const isImported = effectiveTab === 'imported'

  const isReports = effectiveTab === 'reports'
  const isTransactions = effectiveTab === 'transactions'
  // The balance summary compares two bounded points, so it needs a start. The
  // Tokens table itself renders for any period ("All time" reads a "-" start).
  const periodHasStart = !!period?.startDate
  // Value filter is Transactions-only — don't let it suppress the Tokens summary.
  const showSummary =
    periodHasStart &&
    !isLots &&
    !isImported &&
    !isReports &&
    (!isTransactions || !hasValueFilter)

  // The imported and reports tabs have no filters, so the bar is hidden there.
  // The null case also narrows the tab to the modes the filter bar accepts.
  const filterBarTab =
    effectiveTab === 'imported' || effectiveTab === 'reports'
      ? null
      : effectiveTab

  return (
    <TabsPrimitive.Root value={effectiveTab} onValueChange={setTab}>
      <PageLayout
        pageType="table"
        title="Accounting"
        actionButtons={
          isReports ? (
            <NewReportButton />
          ) : isImported ? (
            <ImportAccountButton />
          ) : isLots ? (
            <RecomputeLotsButton recompute={recompute} />
          ) : undefined
        }
        stickyContent={
          <>
            <div className="border-b border-gray-200 pt-4 dark:border-gray-800">
              <TabsList variant="table">
                <TabsTrigger value="transactions">Transactions</TabsTrigger>
                <TabsTrigger value="tokens">Tokens</TabsTrigger>
                {showLots && <TabsTrigger value="lots">Lots</TabsTrigger>}
                {showLots && (
                  <TabsTrigger value="imported">Imported</TabsTrigger>
                )}
                <TabsTrigger value="reports">Reports</TabsTrigger>
              </TabsList>
            </div>
            {filterBarTab && (
              <div className="flex flex-col gap-4 px-4 pb-4 pt-4 md:px-6 lg:px-8">
                <AccountingFilterBar
                  period={period}
                  setPeriod={setPeriod}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  filterParams={filterParams}
                  tab={filterBarTab}
                  tokenSearch={tokenSearch}
                  onTokenSearchChange={setTokenSearch}
                  lotsSearch={lotsSearch}
                  onLotsSearchChange={setLotsSearch}
                  lotsRemaining={lotsRemaining}
                  onLotsRemainingChange={setLotsRemaining}
                  lotsCostBasis={lotsCostBasis}
                  onLotsCostBasisChange={setLotsCostBasis}
                  lotsUnitCost={lotsUnitCost}
                  onLotsUnitCostChange={setLotsUnitCost}
                />
                <PartialNetworkAccountingBanner
                  accountIds={accountIds}
                  // Lots hides the Network filter, so a value left over from
                  // another tab does not count there.
                  hasNetworkFilter={!isLots && (chainIds?.length ?? 0) > 0}
                />
                {showSummary && (
                  <AccountingBalanceSummary
                    period={period}
                    accountIds={accountIds}
                    chainIds={chainIds}
                    // The token filter is Transactions-only; the Tokens tab
                    // summary stays org-wide.
                    tokenKeys={isTransactions ? tokenKeys : undefined}
                    isTokensTab={!isTransactions}
                  />
                )}
              </div>
            )}
          </>
        }
      >
        <TabsContent value="transactions">
          <AccountingTable
            period={period}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
          />
        </TabsContent>
        <TabsContent value="tokens">
          <TokensTab
            period={period}
            accountIds={accountIds}
            chainIds={chainIds}
            search={tokenSearch}
            onFilterTransactionsByToken={filterTransactionsByToken}
            onViewLots={showLots ? openLotsForToken : undefined}
          />
        </TabsContent>
        {showLots && (
          <TabsContent value="lots">
            <LotsTab
              period={period}
              accountIds={accountIds}
              tokenKeys={tokenKeys}
              search={lotsSearch}
              remaining={lotsRemaining}
              costBasis={lotsCostBasis}
              unitCost={lotsUnitCost}
              recompute={recompute}
            />
          </TabsContent>
        )}
        {showLots && (
          <TabsContent value="imported">
            <ImportedAccountsTab />
          </TabsContent>
        )}
        <TabsContent value="reports">
          <ReportsTab />
        </TabsContent>
      </PageLayout>
    </TabsPrimitive.Root>
  )
}

const NewReportButton = () => {
  const { setGlobalModalState } = useAuth()
  const { org } = useActiveOrg()

  return (
    <Button
      onClick={() =>
        setGlobalModalState({ isOpen: true, content: <NewReportDialog /> })
      }
      eventName="accounting-open-new-report-dialog"
      eventProperties={{ orgId: org?.id }}
    >
      New report
    </Button>
  )
}

const ImportAccountButton = () => {
  const { org } = useActiveOrg()
  const orgMember = useActiveOrgMember()

  return (
    <Button
      isDisabled={orgMember?.role !== 'OWNER' || org?.isPersonal}
      disabledReason="Only owners can import accounts"
      href={{
        path: buildCreatePath({
          type: 'imported',
          returnTo: '/accounting?tab=imported',
        }),
      }}
    >
      Import account
    </Button>
  )
}

const AccountingTable = ({
  period,
  columnFilters,
  setColumnFilters,
}: {
  period: Period | null
  columnFilters: ColumnFiltersState
  setColumnFilters: OnChangeFn<ColumnFiltersState>
}) => {
  const { serverGetRequest } = useServerRequests()
  const { org } = useActiveOrg()

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: INITIAL_PAGE_SIZE,
  })

  const filterParams = useMemo(
    () => serializeColumnFilters(columnFilters, period ?? undefined),
    [columnFilters, period],
  )

  // Reset to first page when filters or period change (derived state during render)
  const filterKey = useMemo(() => JSON.stringify(filterParams), [filterParams])
  const prevFilterKeyRef = useRef(filterKey)
  if (prevFilterKeyRef.current !== filterKey) {
    prevFilterKeyRef.current = filterKey
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }

  const { data, isLoading } = useQuery({
    queryKey: [
      QUERY_KEYS.ORG_ACCOUNTING_ITEMS,
      org?.id,
      period?.startDate,
      period?.endDate,
      pagination.pageIndex,
      pagination.pageSize,
      filterParams,
    ],
    queryFn: async ({ queryKey }) => {
      const orgId = queryKey[1] as string | undefined
      const startDate = queryKey[2] as Date | undefined
      const endDate = queryKey[3] as Date | undefined
      const pageIndex = queryKey[4] as number
      const pageSize = queryKey[5] as number
      const filters = queryKey[6] as Record<string, string>

      if (!orgId) throw new Error('Org ID required')

      const response = await serverGetRequest<{
        items: AccountingItem[]
        totalCount?: number
      }>({
        path: '/api/org/accounting/v2',
        params: {
          orgId,
          ...(startDate ? { startDate: startDate.toISOString() } : {}),
          ...(endDate ? { endDate: endDate.toISOString() } : {}),
          pageIndex,
          pageSize,
          ...filters,
        },
        errorMessage: 'Failed to fetch accounting items',
      })
      if (response) {
        return response.data
      }
    },
  })

  const columns = useAccountingColumns()

  const previousTotalCount = usePrevious(data?.totalCount)

  const items = data?.items ?? []

  // Use previous totalCount during loading, otherwise use current
  const totalCount =
    isLoading && !isNil(previousTotalCount)
      ? previousTotalCount
      : (data?.totalCount ?? 0)

  return (
    <Table
      size="small"
      columns={columns as ColumnDef<AccountingItem>[]}
      data={items}
      emptyMessage={`No transactions or transfers found`}
      state={{
        columnFilters,
        pagination,
      }}
      onColumnFiltersChange={setColumnFilters}
      onPaginationChange={setPagination}
      pagination={{
        enabled: true,
        serverSide: true,
        totalCount,
        includeFirstAndLastPage: true,
        initialPageSize: INITIAL_PAGE_SIZE,
      }}
      showColumnFilters={false}
      isLoading={isLoading}
      skeletonCellClassName="h-[34px]"
      skeletonRowCount={pagination.pageSize}
    />
  )
}

const ActionsDropdown = ({
  accountingItem,
}: {
  accountingItem: AccountingItem
}) => {
  const { transactionHash, userOpHash, inboundTransfer, relatedTransaction } =
    accountingItem

  const org = useRequiredActiveOrg()
  const [isOpen, setIsOpen] = useState(false)

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <TableActionTrigger aria-label="Accounting item actions" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {!!transactionHash && (
          <DropdownMenuItem
            onClick={() =>
              copyToClipboard(transactionHash, 'Transaction hash copied')
            }
          >
            <CopyIcon />
            <div>Copy transaction hash</div>
          </DropdownMenuItem>
        )}
        {!!userOpHash && (
          <DropdownMenuItem
            onClick={() => copyToClipboard(userOpHash, 'UserOp hash copied')}
          >
            <CopyIcon />
            <div>Copy user op hash</div>
          </DropdownMenuItem>
        )}
        {/* Lazy load menu items - only render when dropdown is open */}
        {isOpen && (
          <>
            {relatedTransaction && (
              <TransactionDropdownMenuItems
                transaction={
                  {
                    isPublicTransaction: false,
                    isSplitsTransaction: true,
                    orgId: org.id,
                    userOpHash: accountingItem.userOpHash,
                    smartAccountAddress: accountingItem.smartAccountAddress,
                    signers: [],
                    eoaSigners: [],
                    status: TransactionStatus.Succeeded,
                    ...relatedTransaction,
                    chainId: accountingItem.chainId,
                    // AccountingItem.memo is canonical — must override after spread
                    memo: accountingItem.memo,
                  } as unknown as OrgTransaction
                }
              />
            )}
            {inboundTransfer && (
              <TransferDropdownMenuItems
                assetTransfer={{
                  ...inboundTransfer,
                  memo: accountingItem.memo,
                }}
              />
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const serializeColumnFilters = (
  columnFilters: ColumnFiltersState,
  period?: Period,
) => {
  const accountFilter = columnFilters.find(
    (filter) => filter.id === 'smartAccountId',
  )
  const chainFilter = columnFilters.find((filter) => filter.id === 'chainId')
  const tokenFilter = columnFilters.find((filter) => filter.id === 'token')
  const amountFilter = columnFilters.find((filter) => filter.id === 'usdValue')
  const memoFilter = columnFilters.find((filter) => filter.id === 'memo')
  const transactionHashFilter = columnFilters.find(
    (filter) => filter.id === 'transactionHash',
  )

  const params: Record<string, string> = {}

  if (period?.startDate) {
    params.startDate = period.startDate.toISOString()
  }
  if (period?.endDate) {
    params.endDate = period.endDate.toISOString()
  }

  if (accountFilter && accountFilter.value) {
    const parsedFilterValue = parseMultiSelectFilterValue(accountFilter.value)
    if (!isError(parsedFilterValue) && parsedFilterValue.length > 0) {
      params.accountIds = parsedFilterValue.join(',')
    }
  }

  if (chainFilter && chainFilter.value) {
    const parsedFilterValue = parseMultiSelectFilterValue(chainFilter.value)
    if (!isError(parsedFilterValue) && parsedFilterValue.length > 0) {
      params.chainIds = parsedFilterValue.join(',')
    }
  }

  if (tokenFilter && tokenFilter.value) {
    const parsedFilterValue = parseMultiSelectFilterValue(tokenFilter.value)
    if (!isError(parsedFilterValue) && parsedFilterValue.length > 0) {
      params.tokens = parsedFilterValue.join(',')
    }
  }

  if (amountFilter && amountFilter.value) {
    const parsedDirectionAndValue = parseDirectionAndValue(amountFilter.value)
    if (!isError(parsedDirectionAndValue)) {
      if (
        !parsedDirectionAndValue.direction.inbound ||
        !parsedDirectionAndValue.direction.outbound
      ) {
        if (parsedDirectionAndValue.direction.inbound !== undefined) {
          params.inbound = parsedDirectionAndValue.direction.inbound.toString()
        }
        if (parsedDirectionAndValue.direction.outbound !== undefined) {
          params.outbound =
            parsedDirectionAndValue.direction.outbound.toString()
        }
      }

      if (parsedDirectionAndValue.min && parsedDirectionAndValue.min !== '') {
        params.minAmount = parsedDirectionAndValue.min
      }
      if (parsedDirectionAndValue.max && parsedDirectionAndValue.max !== '') {
        params.maxAmount = parsedDirectionAndValue.max
      }
    }
  }

  if (memoFilter && memoFilter.value && typeof memoFilter.value === 'string') {
    params.memo = memoFilter.value
  }

  if (
    transactionHashFilter &&
    transactionHashFilter.value &&
    typeof transactionHashFilter.value === 'string'
  ) {
    params.transactionHash = transactionHashFilter.value
  }

  return params
}
export const AccountingPage = withAuthentication(memo(AccountingPageInner))
